import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import axios from "axios"
import { API_BASE } from "../../app/const"
import { RootState } from "../../app/store"
import { Page, Pageable, defaultPageContent } from "../admin/AdminSlice"
import { Authority, InvitedUsers, UserRole, fireauth } from "../auth/AuthSlice"

export interface AdminOrg {
  id: number
  name: string
  ownerId: string
  ownerEmail: string
  autoAddEmailPattern: string
  paidTier: number
  paidTierCount: number
  refillCount: number
  paidTierExpiry: number
}

export interface AdminOrgState {
  status: 'idle' | 'loading' | 'failed'
  org: AdminOrg
  orgs: Page<AdminOrg>
  orgUsers: Page<UserRole>
  invitedUsers: Page<InvitedUsers>
  autoAddResult?: {
    success: UserRole[],
    failure: UserRole[]
  }
}

const initialState: AdminOrgState = {
  status: 'idle',
  org: {
    id: 0,
    name: '',
    ownerId: '',
    ownerEmail: '',
    autoAddEmailPattern: '',
    paidTier: 0,
    paidTierCount: 0,
    refillCount: 0,
    paidTierExpiry: 0,
  },
  orgs: defaultPageContent,
  orgUsers: {
    content: [],
    empty: false,
    first: true,
    last: true,
    number: 0,
    numberOfElements: 0,
    pageable: {
      offset: 0,
      pageNumber: 0,
      pageSize: 0,
      paged: false,
      sort: {
        empty: false,
        sorted: false,
        unsorted: false
      },
      unpaged: false
    },
    size: 0,
    sort: {
      empty: false,
      sorted: false,
      unsorted: false
    },
    totalElements: 0,
    totalPages: 0
  },
  invitedUsers: {
    content: [],
    empty: false,
    first: true,
    last: true,
    number: 0,
    numberOfElements: 0,
    pageable: {
      offset: 0,
      pageNumber: 0,
      pageSize: 0,
      paged: false,
      sort: {
        empty: false,
        sorted: false,
        unsorted: false
      },
      unpaged: false
    },
    size: 0,
    sort: {
      empty: false,
      sorted: false,
      unsorted: false
    },
    totalElements: 0,
    totalPages: 0
  },
}
const isAdmin = (userAuthorities: [Authority] | undefined) => {
  if (userAuthorities) {
    return userAuthorities.some(authority => authority.authority === 'ROLE_ADMIN')
  }
  return false
}
export const fetchAllAdminOrgsAsync = createAsyncThunk(
  'account/fetchAllAdminOrgsAsync', async ({ mode, userAuthorities }: { mode: string, userAuthorities: [Authority] | undefined }, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const response = await axios.get(`${API_BASE}apps/org/${userAuthorities && userAuthorities.some(e => e.authority === 'ROLE_ADMIN') ? 'all' : 'my'}?size=500`, {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      })
      return response.data
    }
  }
)

export const saveAdminOrgAsync = createAsyncThunk(
  'admin/saveAdminOrgAsync', async ({ adminOrg, mode }: { adminOrg: AdminOrg, mode: string }, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const headers = {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      }
      const response = (mode === 'edit') ? await axios.put(`${API_BASE}apps/org/${adminOrg.id}`, adminOrg, headers)
        : (mode === 'delete') ? await axios.delete(`${API_BASE}apps/org/${adminOrg.id}`, headers)
          : await axios.post(`${API_BASE}apps/org/create`, adminOrg, headers)
      return response.data
    }
  }
)

export const updateOrgUserPaidTierLimitAsync = createAsyncThunk('admin/updateOrgUserPaidTierLimitAsync',
  async ({ orgId, userEmail, paidTierLimit }: { orgId: string, userEmail: string, paidTierLimit: number | undefined }, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const response = paidTierLimit ? await axios.put(`${API_BASE}apps/org/${orgId}/users/${userEmail}/orgPaidTierCount/${paidTierLimit}`, {}, {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      }) :
        await axios.delete(`${API_BASE}apps/org/${orgId}/users/${userEmail}/orgPaidTierCount`, {
          headers: {
            "Content-Type": "application/json; charset=UTF-8",
            "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
          }
        })
      return response.data
    }
  })

export const fetchAllUsersOfOrgAsync = createAsyncThunk('admin/fetchAllUsersOfOrgAsync', async ({ orgId, page }: { orgId: string, page: Pageable }, { getState }) => {
  const state = getState() as RootState
  if (state) {
    const response = await axios.get(`${API_BASE}apps/org/${orgId}/users`, {
      params: page,
      headers: {
        "Content-Type": "application/json; charset=UTF-8",
        "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
      }
    })
    return response.data
  }
})

export const fetchInvitedUsersOfOrgAsync = createAsyncThunk('admin/fetchInvitedUsersOfOrgAsync', async ({ orgId, page }: { orgId: String, page: Pageable }, { getState }) => {
  const state = getState() as RootState
  if (state) {
    const response = await axios.get(`${API_BASE}apps/invited`, {
      params: page,
      headers: {
        "Content-Type": "application/json; charset=UTF-8",
        "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
      }
    })
    return response.data
  }
})

export const removeUserFromOrgAsync = createAsyncThunk('admin/removeUserFromOrgAsync', async ({ orgId, userEmail }: { orgId: string, userEmail: string }, { getState }) => {
  const state = getState() as RootState
  if (state) {
    const response = await axios.delete(`${API_BASE}apps/org/${orgId}/users/${userEmail}`, {
      headers: {
        "Content-Type": "application/json; charset=UTF-8",
        "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
      }
    })
    return response.data
  }
})

export const addUserToOrgAsync = createAsyncThunk('admin/addUserToOrgAsync', async ({ orgId, role, email }: { orgId: string, role: string, email: string }, { getState }) => {
  const state = getState() as RootState
  if (state) {
    const response = await axios.post(`${API_BASE}apps/org/${orgId}/users`, { role, email }, {
      headers: {
        "Content-Type": "application/json; charset=UTF-8",
        "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
      }
    })
    return response.data
  }
})


export const autoAddUserToOrgAsync = createAsyncThunk('admin/autoAddUserToOrgAsync', async ({ orgId, role, isDryRun, isAutoAddFutureUsers }: { orgId: string, role: string, isDryRun: boolean, isAutoAddFutureUsers: boolean }, { getState }) => {
  const state = getState() as RootState
  if (state) {
    const response = await axios.post(`${API_BASE}apps/org/${orgId}/autoAddUsers`, { role, isDryRun, isAutoAddFutureUsers }, {
      headers: {
        "Content-Type": "application/json; charset=UTF-8",
        "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
      }
    })
    return { response: response.data, request: { orgId, role, isDryRun, isAutoAddFutureUsers } }
  }
})


export const adminOrgSlice = createSlice({
  name: 'admin-org',
  initialState,
  reducers: {
    resetAdminOrg: (state) => {
      state.status = 'idle'
      state.org = initialState.org
      state.orgs = initialState.orgs
      state.orgUsers = initialState.orgUsers
    },
    pickAdminOrg: (state, action) => {
      state.status = 'idle'
      state.org = action.payload
    }
  },
  extraReducers: (builder) => {
    builder.
      addCase(fetchAllAdminOrgsAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(fetchAllAdminOrgsAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.org = initialState.org
        state.orgs = action.payload
      }).addCase(fetchAllAdminOrgsAsync.rejected, (state, action) => {
        state.status = 'failed'
        state.org = initialState.org
        state.orgs = initialState.orgs
      }).addCase(saveAdminOrgAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(saveAdminOrgAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.org = action.payload
      }).addCase(saveAdminOrgAsync.rejected, (state, action) => {
        state.status = 'failed'
        state.org = initialState.org
      }).addCase(fetchAllUsersOfOrgAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(fetchAllUsersOfOrgAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.orgUsers = action.payload
      }).addCase(fetchAllUsersOfOrgAsync.rejected, (state, action) => {
        state.status = 'failed'
        state.orgUsers = initialState.orgUsers
      }).addCase(fetchInvitedUsersOfOrgAsync.pending, (state, action) => {
        state.status = 'loading'
      }).addCase(fetchInvitedUsersOfOrgAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.invitedUsers = action.payload
      }).addCase(fetchInvitedUsersOfOrgAsync.rejected, (state, action) => {
        state.status = 'failed'
      }).addCase(removeUserFromOrgAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(removeUserFromOrgAsync.fulfilled, (state, action) => {
        state.status = 'idle'
      }).addCase(removeUserFromOrgAsync.rejected, (state, action) => {
        state.status = 'failed'
      }).addCase(addUserToOrgAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(addUserToOrgAsync.fulfilled, (state, action) => {
        state.status = 'idle'
      }).addCase(addUserToOrgAsync.rejected, (state, action) => {
        state.status = 'failed'
      }).addCase(autoAddUserToOrgAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(autoAddUserToOrgAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        if (action.payload?.request?.isDryRun) {
          state.autoAddResult = action.payload?.response
        } else {
          state.autoAddResult = undefined
        }
      }).addCase(autoAddUserToOrgAsync.rejected, (state, action) => {
        state.status = 'failed'
        state.autoAddResult = undefined
      })
  }
})

export const { resetAdminOrg, pickAdminOrg } = adminOrgSlice.actions
export const selectAllAdminOrgs = (state: RootState) => state.adminOrg.orgs
export const selectAdminOrg = (state: RootState) => state.adminOrg.org
export const selectAdminOrgUsers = (state: RootState) => state.adminOrg.orgUsers
export const selectAdminOrgStatus = (state: RootState) => state.adminOrg.status
export const selectAutoAddResult = (state: RootState) => state.adminOrg.autoAddResult
export const selectInvitedUsers = (state: RootState) => state.adminOrg.invitedUsers
export default adminOrgSlice.reducer